import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { DecisionBriefOverview } from "./ui/overview/DecisionBriefOverview";
import { ProjectLoadingScreen } from "../ProjectLoadingScreen";
import { ChapterQueuedScreen, TQueuedPill } from "../GenerationStates/ChapterQueuedScreen";
import { HomeGeneratingSkeleton } from "../GenerationStates/HomeGeneratingSkeleton";

type TProjectOverviewIslandProps = {
  isGenerationResolved?: boolean;
};

export function ProjectOverviewIsland({ isGenerationResolved = true }: TProjectOverviewIslandProps) {
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const { selectedProject } = useProjectStore();
  const { t } = useTranslation("projectHome");
  const {
    isSetupActive,
    isSettingUp,
    isGenerationDataPending,
    isHomeGenerating,
    homeBlockerSections,
    homeBlockerChapters
  } = useProjectGenerationState(projectId);

  // The overview must not render before we know whether the project is generating —
  // otherwise the finished Home flashes for a few seconds and is replaced. The same
  // holds once a task is known but its status is not: pick a screen too early and it
  // is swapped for another one a moment later.
  if (!selectedProject || !isGenerationResolved || isGenerationDataPending) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <ProjectLoadingScreen />
      </div>
    );
  }

  if (isSetupActive) {
    // "Setting up" keeps the page visible under a dim and floats its card above it
    // (page-level overlay in ProjectDetailsPage), so this panel stays empty.
    if (isSettingUp) return <div className="w-full h-full" />;
    if (isHomeGenerating) return <HomeGeneratingSkeleton />;

    const pills: TQueuedPill[] = [
      ...homeBlockerSections.map((section) => ({
        key: section.key,
        name: t(section.labelKey),
        IconComponent: section.Icon
      })),
      ...homeBlockerChapters.map((chapter) => ({
        key: chapter.projectTemplateChapter.slug || String(chapter.id),
        name: chapter.projectTemplateChapter.name,
        icon: chapter.projectTemplateChapter.icon
      }))
    ];
    return <ChapterQueuedScreen title={t("sidebar.home")} pills={pills} />;
  }

  return <DecisionBriefOverview />;
}
